import { colours } from "@affinity/constants/StyleConstants";
import { breakpointWidth, rem } from "@affinity/constants/StyleHelpers";
import Icon from "@insite/mobius/Icon";
import * as React from "react";
import styled from "styled-components";

type StepIndicatorProps = {
    currentStep: number;
    labels: Record<number, string>;
};

export const HorizontalStepIndicator: React.FC<StepIndicatorProps> = ({ currentStep, labels }) => {
    // generate array of steps and sort it
    const steps = Object.keys(labels)
        .map(Number)
        .sort((a, b) => a - b);

    return (
        <StepsList>
            {steps.map((step, index) => {
                const state = step < currentStep ? "complete" : step === currentStep ? "active" : "incomplete";
                return (
                    <Step key={index}>
                        <StepIndicator state={state}>
                            {state !== "complete" && step}
                            {state === "complete" && <Icon src="Check" />}
                        </StepIndicator>
                        <StepLabel>{labels[step]}</StepLabel>
                    </Step>
                );
            })}
        </StepsList>
    );
};

const mobileStepGap = 30;
const desktopStepGap = 70;

const StepsList = styled.ol`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${rem(mobileStepGap)};
    padding: 0;
    margin: 0;
    padding-bottom: ${rem(16)};
    @media (min-width: ${breakpointWidth("md")}) {
        gap: ${rem(desktopStepGap)};
        padding-bottom: ${rem(46)};
    }
    border-bottom: ${rem(3)} solid ${colours.lightGreen20};
`;

const Step = styled.li`
    width: 48px;
    height: 48px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: ${colours.black};
    font-size: ${rem(16)};
    font-weight: 300;
    list-style: none;
`;

const StepLabel = styled.span`
    display: none;
    text-align: center;
    font-size: ${rem(14)};
    margin-top: ${rem(2)};
    color: ${colours.black};
    @media (min-width: ${breakpointWidth("md")}) {
        display: block;
        margin-left: ${rem((desktopStepGap / 2) * -1)};
        margin-right: ${rem((desktopStepGap / 2) * -1)};
    }
`;

const StepIndicator = styled.span<{ state?: "complete" | "active" | "incomplete" }>`
    position: relative;
    width: 48px;
    height: 48px;
    padding: 10px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: ${colours.black};
    font-size: ${rem(16)};
    font-weight: 300;
    border-radius: 50%;
    background-color: ${colours.lightGreen50};
    border: 3px solid ${colours.lightGreen};
    color: ${colours.black};
    ${({ state }) => {
        switch (state) {
            case "complete":
                return `
                background-color: ${colours.green};
                span {
                    width: 16px;
                    height: 16px;
                }
                svg {
                    color: ${colours.white};
                    stroke-width: 3px;
                }
                `;
            case "active":
                return `
                background-color: ${colours.lightGreen};
                font-weight: 600;
                `;
        }
    }}
    &::after {
        position: absolute;
        left: calc(100% + 3px);
        top: 50%;
        content: "";
        display: block;
        width: ${rem(mobileStepGap)};
        height: 3px;
        background-color: ${colours.lightGreen};
        @media (min-width: ${breakpointWidth("md")}) {
            width: ${rem(desktopStepGap)};
        }
    }
    li:last-child &::after {
        display: none;
    }
`;
